/**
 * Transmission line capacity binning with length filtering.
 * - Lines below P25 length (or null capacity/length): light grey, excluded from binning.
 * - ≤5 distinct capacities among eligible lines: one color per value.
 * - >5 distinct values: merge adjacent values into up to 5 bins (~equal counts), then merge small neighbors.
 */

import { smartFormatNumber } from './numberFormatting';

/** Low → high: green, light red, maroon, light blue, dark blue */
export const CAPACITY_BIN_COLORS = ['#22c55e', '#f87171', '#881337', '#60a5fa', '#1e3a8a'];

export const TRANSMISSION_CAPACITY_UNIT = 'MVA';
export const FILTERED_LINE_COLOR = '#6b7280';
export const FILTERED_LINE_OPACITY = 0.82;
export const CAPACITY_FILTERED_LAYER_KEY = 'capacityFiltered';
/** @deprecated use CAPACITY_FILTERED_LAYER_KEY */
export const CAPACITY_UNKNOWN_LAYER_KEY = CAPACITY_FILTERED_LAYER_KEY;
export const CAPACITY_BIN_LAYER_PREFIX = 'capacityBin';
export const MAX_CAPACITY_BINS = 5;
export const DISCRETE_VALUE_MAX = 5;

const DIAGNOSTIC_ISO_CODES = new Set(['CHN', 'IND', 'BRA']);

function paletteIndexForBin(index, count) {
  if (count <= 1) return 0;
  return Math.round((index / (count - 1)) * (CAPACITY_BIN_COLORS.length - 1));
}

export function getCapacityColorForBinIndex(index, count) {
  if (!count || count < 1) return CAPACITY_BIN_COLORS[0];
  return CAPACITY_BIN_COLORS[paletteIndexForBin(index, count)];
}

export function getTransmissionCapacityGradientCss() {
  const segment = 100 / CAPACITY_BIN_COLORS.length;
  return CAPACITY_BIN_COLORS.map((color, i) => {
    const start = (i * segment).toFixed(1);
    const end = ((i + 1) * segment).toFixed(1);
    return `${color} ${start}%, ${color} ${end}%`;
  }).join(', ');
}

export function getCapacityBinLayerKey(binIndex) {
  return `${CAPACITY_BIN_LAYER_PREFIX}${binIndex}`;
}

export function getAllCapacityLayerKeys(binSpec = null) {
  const keys = binSpec?.bins?.length
    ? binSpec.bins.map((bin) => bin.layerKey)
    : Array.from({ length: MAX_CAPACITY_BINS }, (_, i) => getCapacityBinLayerKey(i));

  if (!binSpec || (binSpec.excludedGreyCount ?? 0) > 0) {
    keys.push(CAPACITY_FILTERED_LAYER_KEY);
  }
  return keys;
}

export function isTransmissionCapacityLayerKey(layerKey) {
  return (
    layerKey === CAPACITY_FILTERED_LAYER_KEY ||
    (typeof layerKey === 'string' && layerKey.startsWith(CAPACITY_BIN_LAYER_PREFIX))
  );
}

export function parseLineCapacityMva(line) {
  const raw = line?.capacity ?? line?.s_nom;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function parseLineLengthKm(line) {
  const raw = line?.length ?? line?.length_km;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function capacitiesEqual(a, b) {
  if (a === b) return true;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  const scale = Math.max(Math.abs(a), Math.abs(b), 1);
  return Math.abs(a - b) <= scale * 1e-9;
}

export function percentile(sortedAsc, p) {
  if (!sortedAsc?.length) return null;
  if (sortedAsc.length === 1) return sortedAsc[0];
  const idx = (p / 100) * (sortedAsc.length - 1);
  const lower = Math.floor(idx);
  const upper = Math.ceil(idx);
  if (lower === upper) return sortedAsc[lower];
  const weight = idx - lower;
  return sortedAsc[lower] * (1 - weight) + sortedAsc[upper] * weight;
}

export function formatCapacityValue(value, unit = TRANSMISSION_CAPACITY_UNIT) {
  return `${smartFormatNumber(value)} ${unit}`;
}

export function formatCapacityBinRange(bin, unit = TRANSMISSION_CAPACITY_UNIT) {
  if (!bin) return '';
  const fmt = (v) => smartFormatNumber(v);
  const { min, max, distinctCount } = bin;
  if (min != null && max != null && (distinctCount === 1 || capacitiesEqual(min, max))) {
    return formatCapacityValue(min, unit);
  }
  if (min != null && max != null) {
    return `${fmt(min)} – ${fmt(max)} ${unit}`;
  }
  return '';
}

export function formatBelowP25LegendLabel(spec) {
  const count = spec?.belowP25Count ?? 0;
  if (count <= 0) return null;
  return `Below P25 length (${count} line${count === 1 ? '' : 's'})`;
}

export function formatNullExcludedLegendLabel(spec) {
  const count = spec?.nullExcludedCount ?? 0;
  if (count <= 0) return null;
  return `Missing capacity or length (${count} line${count === 1 ? '' : 's'})`;
}

export function isLineExcludedFromBinning(line, spec) {
  if (!spec) return true;
  const capacity = parseLineCapacityMva(line);
  const length = parseLineLengthKm(line);
  if (capacity === null || length === null) return true;
  if (spec.p25Length != null && length < spec.p25Length) return true;
  return false;
}

function classifyLines(lineList) {
  const lengths = lineList.map(parseLineLengthKm).filter((l) => l !== null).sort((a, b) => a - b);
  const p25Length = lengths.length ? percentile(lengths, 25) : null;

  let belowP25Count = 0;
  let nullExcludedCount = 0;
  const eligibleLines = [];

  lineList.forEach((line) => {
    const capacity = parseLineCapacityMva(line);
    const length = parseLineLengthKm(line);
    if (capacity === null || length === null) {
      nullExcludedCount += 1;
      return;
    }
    if (p25Length != null && length < p25Length) {
      belowP25Count += 1;
      return;
    }
    eligibleLines.push(line);
  });

  return {
    p25Length,
    belowP25Count,
    nullExcludedCount,
    excludedGreyCount: belowP25Count + nullExcludedCount,
    eligibleLines,
  };
}

function countEligibleLinesForValue(eligibleLines, value) {
  let count = 0;
  eligibleLines.forEach((line) => {
    const c = parseLineCapacityMva(line);
    if (c !== null && capacitiesEqual(c, value)) count += 1;
  });
  return count;
}

function buildValueCountPairs(eligibleLines) {
  const unique = [];
  eligibleLines.forEach((line) => {
    const c = parseLineCapacityMva(line);
    if (c === null) return;
    if (!unique.some((u) => capacitiesEqual(u, c))) unique.push(c);
  });
  unique.sort((a, b) => a - b);

  return unique.map((value) => ({
    value,
    count: countEligibleLinesForValue(eligibleLines, value),
  }));
}

function buildBinFromGroups(groups, index) {
  const values = groups.map((g) => g.value);
  const count = groups.reduce((sum, g) => sum + g.count, 0);
  return {
    index,
    distinctValues: values,
    distinctCount: values.length,
    min: values[0],
    max: values[values.length - 1],
    count,
  };
}

function mergeTwoBins(a, b) {
  const distinctValues = [...a.distinctValues, ...b.distinctValues].sort((x, y) => x - y);
  return {
    index: a.index,
    distinctValues,
    distinctCount: distinctValues.length,
    min: distinctValues[0],
    max: distinctValues[distinctValues.length - 1],
    count: a.count + b.count,
  };
}

function partitionAdjacentValues(valueCountPairs, maxBins = MAX_CAPACITY_BINS) {
  const n = valueCountPairs.length;
  if (n === 0) return [];

  if (n <= maxBins) {
    return valueCountPairs.map((pair, index) => buildBinFromGroups([pair], index));
  }

  const counts = valueCountPairs.map((p) => p.count);
  const total = counts.reduce((a, b) => a + b, 0);
  const target = total / maxBins;
  const rawBins = [];
  let start = 0;

  for (let b = 0; b < maxBins; b += 1) {
    const binsRemaining = maxBins - b;
    if (binsRemaining === 1) {
      rawBins.push(buildBinFromGroups(valueCountPairs.slice(start), b));
      break;
    }

    const binsLeft = binsRemaining - 1;
    const minEnd = start + 1;
    const maxEnd = n - binsLeft;

    let bestEnd = minEnd;
    let bestDiff = Infinity;
    for (let end = minEnd; end <= maxEnd; end += 1) {
      const acc = counts.slice(start, end).reduce((sum, c) => sum + c, 0);
      const diff = Math.abs(acc - target);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestEnd = end;
      }
    }

    rawBins.push(buildBinFromGroups(valueCountPairs.slice(start, bestEnd), b));
    start = bestEnd;
  }

  return rawBins;
}

/** Merge adjacent bins when both are smaller than the average bin size. */
function mergeSmallAdjacentBins(rawBins) {
  if (rawBins.length <= 1) return rawBins;

  let bins = rawBins.map((bin, index) => ({ ...bin, index }));
  let changed = true;

  while (changed && bins.length > 1) {
    changed = false;
    const average = bins.reduce((sum, bin) => sum + bin.count, 0) / bins.length;

    for (let i = 0; i < bins.length - 1; i += 1) {
      if (bins[i].count < average && bins[i + 1].count < average) {
        const merged = mergeTwoBins(bins[i], bins[i + 1]);
        bins = [...bins.slice(0, i), merged, ...bins.slice(i + 2)];
        changed = true;
        break;
      }
    }
  }

  return bins.map((bin, index) => ({ ...bin, index }));
}

function finalizeActiveBins(rawBins, mode) {
  const active = rawBins.filter((bin) => bin.count > 0);
  const n = active.length;
  return active.map((bin, index) => {
    const next = {
      ...bin,
      index,
      mode,
      layerKey: getCapacityBinLayerKey(index),
      color: getCapacityColorForBinIndex(index, n),
      totalBins: n,
    };
    return { ...next, rangeLabel: formatCapacityBinRange(next) };
  });
}

/**
 * @param {Array<object>|undefined} lines
 */
export function computeTransmissionLineCapacityBins(lines) {
  const lineList = lines || [];
  const {
    p25Length,
    belowP25Count,
    nullExcludedCount,
    excludedGreyCount,
    eligibleLines,
  } = classifyLines(lineList);

  const valueCountPairs = buildValueCountPairs(eligibleLines);
  const validCount = valueCountPairs.reduce((sum, p) => sum + p.count, 0);
  const distinctCapacities = valueCountPairs.map((p) => p.value);

  if (validCount === 0) {
    return {
      hasData: false,
      mode: null,
      bins: [],
      p25Length,
      belowP25Count,
      nullExcludedCount,
      excludedGreyCount,
      validCount: 0,
      uniqueValueCount: 0,
      distinctCapacities,
    };
  }

  const uniqueValueCount = valueCountPairs.length;
  const mode = uniqueValueCount <= DISCRETE_VALUE_MAX ? 'discrete' : 'grouped';
  const partitioned = partitionAdjacentValues(valueCountPairs, MAX_CAPACITY_BINS);
  const merged = mergeSmallAdjacentBins(partitioned);
  const bins = finalizeActiveBins(merged, mode);

  return {
    hasData: true,
    mode,
    bins,
    p25Length,
    belowP25Count,
    nullExcludedCount,
    excludedGreyCount,
    validCount,
    uniqueValueCount,
    distinctCapacities,
  };
}

export function resolveLineBinIndex(line, capacityBinSpec) {
  if (isLineExcludedFromBinning(line, capacityBinSpec)) return -1;
  const capacity = parseLineCapacityMva(line);
  if (capacity === null || !capacityBinSpec?.hasData) return -1;

  return capacityBinSpec.bins.findIndex((bin) =>
    bin.distinctValues?.some((v) => capacitiesEqual(v, capacity))
  );
}

export function getLineCapacityColor(line, capacityBinSpec) {
  const binIdx = resolveLineBinIndex(line, capacityBinSpec);
  if (binIdx < 0) return FILTERED_LINE_COLOR;
  return capacityBinSpec.bins[binIdx]?.color ?? FILTERED_LINE_COLOR;
}

export function getLineCapacityLayerKey(line, capacityBinSpec) {
  if (isLineExcludedFromBinning(line, capacityBinSpec)) {
    return CAPACITY_FILTERED_LAYER_KEY;
  }
  const binIdx = resolveLineBinIndex(line, capacityBinSpec);
  if (binIdx < 0) return CAPACITY_FILTERED_LAYER_KEY;
  return capacityBinSpec.bins[binIdx]?.layerKey ?? getCapacityBinLayerKey(binIdx);
}

export function getLineRenderStyle(line, capacityBinSpec, fullOpacity = 1) {
  if (isLineExcludedFromBinning(line, capacityBinSpec)) {
    return {
      color: FILTERED_LINE_COLOR,
      opacity: FILTERED_LINE_OPACITY,
      layerKey: CAPACITY_FILTERED_LAYER_KEY,
    };
  }

  const binIdx = resolveLineBinIndex(line, capacityBinSpec);
  if (binIdx < 0 || !capacityBinSpec?.hasData) {
    return {
      color: FILTERED_LINE_COLOR,
      opacity: FILTERED_LINE_OPACITY,
      layerKey: CAPACITY_FILTERED_LAYER_KEY,
    };
  }

  return {
    color: capacityBinSpec.bins[binIdx].color,
    opacity: fullOpacity,
    layerKey: capacityBinSpec.bins[binIdx].layerKey,
  };
}

export function logTransmissionCapacityBinningDiagnostics(countryIso, spec) {
  const iso = countryIso?.toUpperCase();
  if (!iso || !DIAGNOSTIC_ISO_CODES.has(iso) || !spec) return;

  const binEdges = (spec.bins ?? []).map((bin) => ({
    range: bin.rangeLabel,
    min: bin.min,
    max: bin.max,
    distinctValues: bin.distinctValues,
    count: bin.count,
  }));

  console.group(`[Transmission lines] ${iso} capacity binning`);
  console.log('Distinct capacity values (eligible lines):', spec.distinctCapacities ?? []);
  console.log('P25 length threshold (km):', spec.p25Length);
  console.log('Lines filtered by length (< P25):', spec.belowP25Count ?? 0);
  console.log('Lines excluded (null capacity or length):', spec.nullExcludedCount ?? 0);
  console.log('Bin edges:', binEdges);
  console.log('Line counts per bin:', (spec.bins ?? []).map((bin) => bin.count));
  console.groupEnd();
}

export function clearTransmissionCapacityLayerGroups(layerGroups) {
  if (!layerGroups) return;
  Object.keys(layerGroups).forEach((layerKey) => {
    if (isTransmissionCapacityLayerKey(layerKey) && layerGroups[layerKey]) {
      layerGroups[layerKey].clearLayers();
    }
  });
}

export function bringTransmissionCapacityLayersToFront(layerGroups, binSpec = null) {
  if (!layerGroups) return;

  const filtered = layerGroups[CAPACITY_FILTERED_LAYER_KEY];
  if (filtered && typeof filtered.bringToFront === 'function') {
    filtered.bringToFront();
  }

  const binKeys = binSpec?.bins?.length
    ? binSpec.bins.map((bin) => bin.layerKey)
    : Object.keys(layerGroups).filter((key) => key.startsWith(CAPACITY_BIN_LAYER_PREFIX));

  binKeys.forEach((layerKey) => {
    const group = layerGroups[layerKey];
    if (group && typeof group.bringToFront === 'function') {
      group.bringToFront();
    }
  });
}
